<div class="flex flex-col h-full overflow-hidden">
  <!-- Header -->
  <div class="flex flex-col px-4 sm:px-6 py-2.5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 flex-shrink-0">
    <!-- Title and Buttons Row -->
    <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
      <div class="flex items-center gap-2 min-w-0 text-sm">
        <h2 class="font-semibold text-gray-900 shrink-0">Meeting Audit Log</h2>
        @if (projectName() || meetingTopic()) {
          <span class="text-gray-300">—</span>
          @if (projectName()) {
            <span class="text-gray-500 truncate">{{ projectName() }}</span>
          }
          @if (projectName() && meetingTopic()) {
            <span class="text-gray-300">/</span>
          }
          @if (meetingTopic()) {
            <span class="text-gray-600 truncate font-medium">{{ meetingTopic() }}</span>
          }
        }
      </div>
      <div class="flex gap-2 items-center flex-shrink-0">
        @if (!hasError() && auditLogs().length > 0) {
          <div class="relative z-20">
            <button pButton class="p-button-outlined header-action-btn" [class.p-button-info]="isFiltersVisible()" (click)="toggleFilters()">
              <i class="fa-regular fa-filter text-sm mr-2"></i>
              <span class="hidden sm:inline">{{ isFiltersVisible() ? 'Hide Filters' : 'Filters' }}</span>
              <span class="sm:hidden">{{ isFiltersVisible() ? 'Hide' : 'Filter' }}</span>
              @if (hasActiveFilters() && !isFiltersVisible()) {
                <span class="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full ml-1">
                  {{ activeFilterCount() }}
                </span>
              }
            </button>

            <!-- Filters Panel (dropdown overlay) -->
            @if (isFiltersVisible()) {
              <div class="fixed inset-0 z-10" (click)="isFiltersVisible.set(false)"></div>
              <lfx-meeting-audit-filters-panel
                class="absolute right-0 top-full mt-1 z-20"
                [activeFilterCount]="activeFilterCount()"
                [(dateFrom)]="dateFrom"
                [(dateTo)]="dateTo"
                [(selectedAttributes)]="selectedAttributes"
                [(selectedUsers)]="selectedUsers"
                [(selectedActions)]="selectedActions"
                [(selectedResources)]="selectedResources"
                [(selectedRegistrants)]="selectedRegistrants"
                [(selectedAttendees)]="selectedAttendees"
                [attributeOptions]="attributeOptions()"
                [userOptions]="userOptions()"
                [actionOptions]="actionOptions()"
                [resourceOptions]="resourceOptions()"
                [registrantOptions]="registrantOptions()"
                [attendeeOptions]="attendeeOptions()"
                (dateFromChange)="resetPage()"
                (dateToChange)="resetPage()"
                (selectedAttributesChange)="resetPage()"
                (selectedUsersChange)="resetPage()"
                (selectedActionsChange)="resetPage()"
                (selectedResourcesChange)="resetPage()"
                (selectedRegistrantsChange)="resetPage()"
                (selectedAttendeesChange)="resetPage()"
                (filtersCleared)="resetPage()" />
            }
          </div>
          <button pButton class="header-action-btn" title="Export Audit Log" (click)="onExport()">
            <i class="fa-regular fa-download text-sm mr-2"></i>
            <span class="hidden sm:inline">Export</span>
          </button>
        }
      </div>
    </div>
  </div>

  <!-- Content -->
  @if (hasError()) {
    <div class="flex flex-col items-center justify-center py-12 sm:py-20 px-4 sm:px-8 text-center bg-gray-50 flex-1">
      <div class="bg-red-50 rounded-full p-4 sm:p-6 mb-4">
        <i class="fa-regular fa-exclamation-circle text-4xl sm:text-5xl text-red-500"></i>
      </div>
      <p class="text-base sm:text-lg font-semibold text-gray-900 mb-2">Failed to load audit logs</p>
      <p class="text-sm text-gray-500 max-w-md">Please try again later or contact support if the issue persists</p>
    </div>
  } @else {
    @if (loading()) {
      <!-- Loading skeleton -->
      <div class="flex-1 overflow-hidden flex flex-col min-h-0">
        <div class="overflow-auto flex-1 min-h-0">
          <table class="audit-table">
            <ng-container [ngTemplateOutlet]="tableHeader"></ng-container>
            <tbody>
              @for (i of [1, 2, 3, 4, 5]; track $index) {
                <tr>
                  <td class="date-cell">
                    <p-skeleton width="150px" height="14px"></p-skeleton>
                  </td>
                  <td class="user-cell">
                    <div class="flex items-center gap-2">
                      <p-skeleton shape="circle" size="2rem"></p-skeleton>
                      <div class="flex flex-col gap-1">
                        <p-skeleton width="100px" height="14px"></p-skeleton>
                        <p-skeleton width="130px" height="11px"></p-skeleton>
                      </div>
                    </div>
                  </td>
                  <td class="action-cell"><p-skeleton width="100px" height="14px"></p-skeleton></td>
                  <td class="resource-cell"><p-skeleton width="90px" height="14px"></p-skeleton></td>
                  <td class="field-cell"><p-skeleton width="80px" height="14px"></p-skeleton></td>
                  <td class="value-cell"><p-skeleton width="120px" height="13px"></p-skeleton></td>
                  <td class="value-cell"><p-skeleton width="110px" height="13px"></p-skeleton></td>
                  <td class="details-cell">
                    <div class="flex flex-col gap-2">
                      <div class="detail-item">
                        <p-skeleton width="80px" height="12px" styleClass="mb-1"></p-skeleton>
                        <p-skeleton width="100px" height="13px"></p-skeleton>
                      </div>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    } @else {
      <div class="flex flex-col flex-1 bg-white overflow-hidden">
        @if (filteredAuditLogs().length > 0) {
          <!-- Table View -->
          <div class="flex-1 overflow-hidden flex flex-col min-h-0">
            <div #scrollContainer class="overflow-auto flex-1 min-h-0">
              <table class="audit-table">
                <ng-container [ngTemplateOutlet]="tableHeader"></ng-container>
                <tbody>
                  @for (group of groupedPaginatedLogs(); track group.groupId) {
                    @for (log of group.logs; track log.id) {
                      <tr [class.action-changed-system]="!log.effectiveUser" [class.action-changed-user]="!!log.effectiveUser">
                        <!-- Date & Time -->
                        <td class="date-cell">
                          <span class="text-sm text-gray-900 whitespace-nowrap"
                            >{{ log.created_at | date: 'MMM d, y' }} &bull; {{ log.created_at | date: 'h:mm a' }}</span
                          >
                        </td>

                        <!-- User -->
                        <td class="user-cell">
                          @if (log.effectiveUser; as effectiveUser) {
                            <div class="flex items-center gap-2">
                              @if (effectiveUser.profile_picture) {
                                <p-avatar
                                  [image]="effectiveUser.profile_picture"
                                  (onImageError)="onImageError($event)"
                                  shape="circle"
                                  size="normal"
                                  styleClass="avatar-image-fit"
                                  [style]="{ 'min-width': '32px', 'min-height': '32px', width: '32px', height: '32px' }"></p-avatar>
                              } @else {
                                <p-avatar
                                  [label]="effectiveUser.name || 'S' | initial"
                                  shape="circle"
                                  size="normal"
                                  [style]="{ 'min-width': '32px', 'min-height': '32px', width: '32px', height: '32px' }"></p-avatar>
                              }
                              <div class="flex flex-col">
                                <span class="text-sm font-medium text-gray-900 leading-tight">{{ effectiveUser.name || 'System' }}</span>
                                @if (effectiveUser.email) {
                                  <span class="text-xs text-gray-500 leading-tight">{{ effectiveUser.email }}</span>
                                }
                              </div>
                            </div>
                          } @else {
                            <div class="flex items-center gap-2">
                              <div class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <i class="fa-regular fa-server text-gray-500 text-sm"></i>
                              </div>
                              <span class="text-sm font-medium text-gray-900">System</span>
                            </div>
                          }
                        </td>

                        <!-- Action -->
                        <td class="action-cell">
                          <span class="text-sm font-medium" [class]="log.actionCssClass">
                            {{ log.action }}
                          </span>
                        </td>

                        <!-- Resource -->
                        <td class="resource-cell">
                          <span class="text-sm text-gray-700">{{ log.formattedResourceName }}</span>
                        </td>

                        <!-- Field -->
                        <td class="field-cell">
                          <span class="text-sm font-semibold text-gray-900" [pTooltip]="log.attribute_name | auditFieldTooltip" tooltipPosition="top">
                            {{ log.attribute_name | auditFieldLabel }}
                          </span>
                        </td>

                        <!-- Previous Value -->
                        <td class="value-cell">
                          @if (log.attribute_old_value | auditValue: log.attribute_name : false : log.meeting || log.past_meeting; as formattedValue) {
                            @if (isUrlAttribute(log.attribute_name, formattedValue)) {
                              <a [href]="formattedValue" target="_blank" class="text-blue-600 hover:underline">{{ formattedValue }}</a>
                            } @else {
                              <div class="relative group">
                                <div
                                  class="value-content text-gray-600"
                                  [innerHTML]="formattedValue"
                                  [pTooltip]="log.attribute_name === 'agenda' ? (log.attribute_old_value ?? '') : ''"
                                  tooltipPosition="top"></div>
                                @if (log.attribute_name === 'agenda' && log.attribute_old_value) {
                                  <button
                                    pButton
                                    class="p-button-text p-button-sm p-button-rounded absolute top-0 -right-3 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity !p-1"
                                    (click)="onCopyToClipboard(log.attribute_old_value)"
                                    title="Copy agenda to clipboard">
                                    <i class="fa-regular fa-copy text-xs"></i>
                                  </button>
                                }
                              </div>
                            }
                          }
                        </td>

                        <!-- New Value -->
                        <td class="value-cell">
                          @if (log.attribute_new_value | auditValue: log.attribute_name : false : log.meeting || log.past_meeting; as formattedValue) {
                            @if (isUrlAttribute(log.attribute_name, formattedValue)) {
                              <a [href]="formattedValue" target="_blank" class="text-blue-600 hover:underline font-semibold">{{ formattedValue }}</a>
                            } @else {
                              <div class="relative group">
                                <div
                                  class="value-content text-gray-900 font-semibold"
                                  [innerHTML]="formattedValue"
                                  [pTooltip]="log.attribute_name === 'agenda' ? (log.attribute_new_value ?? '') : ''"
                                  tooltipPosition="top"></div>
                                @if (log.attribute_name === 'agenda' && log.attribute_new_value) {
                                  <button
                                    pButton
                                    class="p-button-text p-button-sm p-button-rounded absolute top-0 -right-3 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity !p-1"
                                    (click)="onCopyToClipboard(log.attribute_new_value)"
                                    title="Copy agenda to clipboard">
                                    <i class="fa-regular fa-copy text-xs"></i>
                                  </button>
                                }
                              </div>
                            }
                          }
                        </td>

                        <!-- Details -->
                        <td class="details-cell">
                          <div class="flex flex-col gap-2">
                            @if (log.showRegistrantInfo) {
                              <div class="detail-item">
                                <div class="detail-header">
                                  <i class="fa-regular fa-user text-purple-500"></i>
                                  <span>Registrant</span>
                                </div>
                                <div class="detail-content flex flex-col">
                                  <span>{{ log.registrantDisplayName }}</span>
                                  @if (log.registrant?.email && log.registrant?.email !== log.registrantDisplayName) {
                                    <span class="text-xs text-gray-500">{{ log.registrant?.email }}</span>
                                  }
                                </div>
                              </div>
                            }

                            @if (log.showDeletedRegistrantInfo) {
                              <div class="detail-item">
                                <div class="detail-header">
                                  <i class="fa-regular fa-user-slash text-red-500"></i>
                                  <span>Deleted Registrant</span>
                                </div>
                                <div class="detail-content flex flex-col">
                                  <span>{{ log.deletedRegistrantDisplayName }}</span>
                                  @if (log.meeting?.deleted_registrant?.email && log.meeting?.deleted_registrant?.email !== log.deletedRegistrantDisplayName) {
                                    <span class="text-xs text-gray-500">{{ log.meeting?.deleted_registrant?.email }}</span>
                                  }
                                </div>
                              </div>
                            }

                            @if (log.showAttendeeInfo) {
                              <div class="detail-item">
                                <div class="detail-header">
                                  <i class="fa-regular fa-user-check text-green-500"></i>
                                  <span>Attendee</span>
                                </div>
                                <div class="detail-content flex flex-col">
                                  @if (log.attendeeDisplayName) {
                                    <span>{{ log.attendeeDisplayName }}</span>
                                    @if (log.attendee?.email && log.attendee?.email !== log.attendeeDisplayName) {
                                      <span class="text-xs text-gray-500">{{ log.attendee?.email }}</span>
                                    }
                                  }
                                </div>
                              </div>
                            }

                            @if (log.showInviteeInfo) {
                              <div class="detail-item">
                                <div class="detail-header">
                                  <i class="fa-regular fa-envelope text-blue-600"></i>
                                  <span>Invitee</span>
                                </div>
                                <div class="detail-content flex flex-col">
                                  @if (log.inviteeDisplayName) {
                                    <span>{{ log.inviteeDisplayName }}</span>
                                    @if (log.invitee?.email && log.invitee?.email !== log.inviteeDisplayName) {
                                      <span class="text-xs text-gray-500">{{ log.invitee?.email }}</span>
                                    }
                                  }
                                </div>
                              </div>
                            }

                            @if (log.showArtifactInfo) {
                              <div class="detail-item">
                                <div class="detail-header">
                                  <i class="fa-regular fa-file text-teal-500"></i>
                                  <span>Artifact</span>
                                </div>
                                <div class="detail-content">
                                  @if (log.artifact?.name) {
                                    <span>{{ log.artifact?.name }}</span>
                                  }
                                  @if (log.artifact?.category) {
                                    <span class="text-gray-500">{{ log.artifact?.category }}</span>
                                  }
                                  @if (log.artifact?.link) {
                                    <a [href]="log.artifact?.link" target="_blank" class="text-blue-600 hover:underline text-xs">View Artifact</a>
                                  }
                                </div>
                              </div>
                            }

                            @if (log.showRecordingInfo) {
                              <div class="detail-item">
                                <div class="detail-header">
                                  <i class="fa-regular fa-video text-red-500"></i>
                                  <span>Recording</span>
                                </div>
                                <div class="detail-content">
                                  @if (log.recording?.topic) {
                                    <span>{{ log.recording?.topic }}</span>
                                  }
                                  @if (log.recording?.recording_access) {
                                    <span class="text-gray-500">Access: {{ log.recording?.recording_access }}</span>
                                  }
                                </div>
                              </div>
                            }

                            @if (log.showSummaryInfo) {
                              <div class="detail-item">
                                <div class="detail-header">
                                  <i class="fa-regular fa-file-lines text-cyan-500"></i>
                                  <span>Summary</span>
                                </div>
                                <div class="detail-content">
                                  @if (log.summary?.summary_title) {
                                    <span>{{ log.summary?.summary_title }}</span>
                                  }
                                  @if (log.summary?.meeting_topic) {
                                    <span class="text-gray-500">{{ log.summary?.meeting_topic }}</span>
                                  }
                                </div>
                              </div>
                            }

                            @if (log.showNoDetailsPlaceholder) {
                              <span class="text-gray-500">—</span>
                            }
                          </div>
                        </td>
                      </tr>
                    }
                  }
                </tbody>
              </table>
            </div>
          </div>
          <div class="flex items-center justify-between px-4 sm:px-6 py-2 bg-white border-t border-gray-200 flex-shrink-0">
            @if (hasActiveFilters()) {
              <span class="text-xs text-gray-500 shrink-0">
                <i class="fa-regular fa-filter mr-1"></i>{{ filteredAuditLogs().length }} of {{ auditLogs().length }}
              </span>
            }
            <p-paginator
              class="flex-1"
              [first]="paginatorFirst()"
              [rows]="pageSize()"
              [totalRecords]="filteredAuditLogs().length"
              [rowsPerPageOptions]="rowsPerPageOptions"
              [showCurrentPageReport]="true"
              currentPageReportTemplate="{first}–{last} of {totalRecords}"
              (onPageChange)="onPageChange($event)">
            </p-paginator>
          </div>
        } @else {
          <!-- Empty state -->
          <div class="flex flex-col items-center justify-center py-12 sm:py-20 px-4 sm:px-8 text-center bg-gray-50">
            <div class="bg-white rounded-full p-4 sm:p-6 mb-4 shadow-sm">
              <i class="fa-regular fa-clipboard-list text-4xl sm:text-5xl text-gray-400"></i>
            </div>
            <p class="text-base sm:text-lg font-semibold text-gray-900 mb-2">No audit logs found</p>
            @if (hasActiveFilters()) {
              <p class="text-sm text-gray-500 max-w-md">Try adjusting your filters to see more results</p>
            } @else {
              <p class="text-sm text-gray-500 max-w-md">There are no audit logs available for this meeting</p>
            }
          </div>
        }
      </div>
    }
  }
</div>

<!-- Shared table header template -->
<ng-template #tableHeader>
  <thead>
    <tr>
      <th scope="col">
        <div class="flex items-center gap-1.5"><i class="fa-regular fa-calendar-clock"></i><span>Date & Time</span></div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5"><i class="fa-regular fa-user"></i><span>User</span></div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5"><i class="fa-regular fa-bolt"></i><span>Action</span></div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5"><i class="fa-regular fa-cube"></i><span>Resource</span></div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5">
          <i class="fa-regular fa-tag"></i>
          <span>Field</span>
          <i class="fa-light fa-info-circle text-blue-600" [pTooltip]="fieldColumnTooltip" tooltipPosition="top"></i>
        </div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5"><i class="fa-regular fa-arrow-left"></i><span>Previous Value</span></div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5"><i class="fa-regular fa-arrow-right"></i><span>New Value</span></div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5"><i class="fa-regular fa-info-circle"></i><span>Details</span></div>
      </th>
    </tr>
  </thead>
</ng-template>
